// Library methods
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import DevicesHeader from "../../shared/DevicesHeader/DevicesHeader";
import DeviceCard from "../../shared/DeviceCard/DeviceCard";

// Contexts
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

type DashboardDevicesType = {
  // Reported by Dashboard.tsx once RecentlyCompletedAccordion and
  // TodayAccordion have both finished loading their real data
  isExamsColumnReady: boolean;
};

const DashboardDevices = ({ isExamsColumnReady }: DashboardDevicesType) => {
  const { t } = useTranslation();
  const { devices, isLoadingDevices } = useDevicesContext();

  const [isActive, setIsActive] = useState("all");

  const sortedDevices = useMemo(
    () => devices?.sort((a, b) => a?.alias?.localeCompare(b?.alias)),
    [devices]
  );

  const filteredDevices = useMemo(() => {
    switch (isActive) {
      case "linked":
        return sortedDevices?.filter((device) => device?.associatedExam);
      case "unlinked":
        return sortedDevices?.filter((device) => !device?.associatedExam);
      default:
        return sortedDevices;
    }
  }, [isActive, sortedDevices]);

  const listMinHeight = useMemo(() => 420, []);

  // Max height = space left below this list's top, capped against the
  // page (stable) rather than the sidebar (which can grow to fit this list).
  const deviceListRef = useRef<HTMLDivElement>(null);
  const [deviceListMaxHeight, setDeviceListMaxHeight] = useState(listMinHeight);

  const updateDeviceListMaxHeight = useCallback(() => {
    const pageContent = document.querySelector("#dashboard-page-content");
    const list = deviceListRef.current;

    if (!pageContent || !list) return;

    const availableHeight =
      pageContent.getBoundingClientRect().bottom -
      list.getBoundingClientRect().top;

    setDeviceListMaxHeight(
      availableHeight > listMinHeight ? availableHeight : listMinHeight
    );
  }, [listMinHeight]);

  // Deferred a frame so it reads layout after the browser has actually
  // applied it, rather than mid-reflow (e.g. right as a breakpoint flips).
  const scheduleUpdate = useCallback(() => {
    requestAnimationFrame(updateDeviceListMaxHeight);
  }, [updateDeviceListMaxHeight]);

  useEffect(() => {
    window.addEventListener("resize", scheduleUpdate);

    // Catches layout changes (like browser zoom) more reliably than the
    // window "resize" event alone.
    const pageContent = document.querySelector("#dashboard-page-content");
    const resizeObserver = pageContent
      ? new ResizeObserver(scheduleUpdate)
      : undefined;
    if (pageContent) resizeObserver?.observe(pageContent);

    return () => {
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
    };
  }, [scheduleUpdate]);

  // Re-measure once the list actually has content to measure -- before
  // that, deviceListRef is empty and this is a no-op.
  useEffect(() => {
    if (!isLoadingDevices && isExamsColumnReady) updateDeviceListMaxHeight();
  }, [isLoadingDevices, isExamsColumnReady, updateDeviceListMaxHeight]);

  return (
    <>
      <DevicesHeader
        showButtons={Boolean(devices && devices?.length > 1)}
        isActive={isActive}
        setIsActive={setIsActive}
      />
      {isLoadingDevices || !isExamsColumnReady ? (
        <Box gap="8px" pb="8px" display="flex" height={listMinHeight}>
          <Skeleton
            variant="rounded"
            width="100%"
            sx={{
              height: { xs: "250px", md: "100%" },
              borderRadius: "8px",
            }}
          />
        </Box>
      ) : (
        <Box
          ref={deviceListRef}
          gap="8px"
          pb="8px"
          display="flex"
          sx={
            filteredDevices && filteredDevices?.length > 1
              ? {
                  px: "0px",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", md: "center" },
                  overflowY: { xs: "hidden", md: "auto" },
                  overflowX: { xs: "auto", md: "hidden" },
                  flexWrap: { xs: "nowrap", md: "wrap" },
                  maxHeight: { xs: listMinHeight, md: deviceListMaxHeight },
                }
              : {}
          }
        >
          {filteredDevices && filteredDevices?.length > 0 ? (
            filteredDevices?.map((device) => {
              return <DeviceCard key={device.id} device={device} />;
            })
          ) : (
            <Box
              width="100%"
              height="218px"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography>{t("No Devices Found.")}</Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default DashboardDevices;
